/**
 * GoalDescriptionEditor — TipTap rich-text editor for Mini-Project
 * goal descriptions (DEV-1075 Phase 7.3a).
 *
 * Output format: JSON (TipTap's getJSON() — ProseMirror document
 * shape, JSON.stringify'd at the boundary). The unified kpi_config
 * row stores it in description_rich (JSONB column). The plain-text
 * projection used for snippets and future semantic search is derived
 * server-side via TipTapJsonHelpers.FlattenToPlainText (Phase 5.4) —
 * no need to maintain it on the client.
 *
 * Toolbar items locked in the 2026-05-24 mockup session:
 *   bold, italic, link, bullet list, ordered list, blockquote.
 * Headings deliberately disabled — goal descriptions are short pitch
 * paragraphs, not how-to docs.
 *
 * Self-contained TipTap config (not extracted from StatusWriter or
 * BlogEditor). DEV-1075 Phase 7.3a explicitly accepted this duplication
 * to keep the slice focused; the unification of the 4 editor configs
 * (status narrative, blog body, section body, goal description) is
 * filed as a separate tech-debt ticket for later.
 */

import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import styles from "./GoalDescriptionEditor.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  /** Serialized TipTap JSON. null/empty/unparseable → blank editor. */
  value: string | null;
  /** Fires on every keystroke with the serialized JSON. Throttled by
   *  TipTap internally — typical throttle is ~one update per
   *  composition. */
  onChange: (json: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

/* ── Inline icons (matches StatusWriter / no external deps) ─────── */

const BoldIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 4h8a4 4 0 0 1 0 8H6z" />
    <path d="M6 12h9a4 4 0 0 1 0 8H6z" />
  </svg>
);

const ItalicIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="19" y1="4" x2="10" y2="4" />
    <line x1="14" y1="20" x2="5" y2="20" />
    <line x1="15" y1="4" x2="9" y2="20" />
  </svg>
);

const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const BulletListIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const OrderedListIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="10" y1="6" x2="21" y2="6" />
    <line x1="10" y1="12" x2="21" y2="12" />
    <line x1="10" y1="18" x2="21" y2="18" />
    <path d="M4 6h1v4" />
    <path d="M4 10h2" />
    <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
  </svg>
);

const QuoteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.75-2-2-2H4c-1.25 0-2 .75-2 2v6c0 1.25.75 2 2 2h3" />
    <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.75-2-2-2h-4c-1.25 0-2 .75-2 2v6c0 1.25.75 2 2 2h3" />
  </svg>
);

/* ── Helpers ───────────────────────────────────────────────────── */

/** Parse the stored JSON string into a TipTap content object.
 *  Defensive: empty / null / malformed → empty doc so the editor
 *  doesn't crash on bad data. */
function parseStoredContent(raw: string | null): object | string {
  if (!raw || raw.trim() === "") return "";
  try {
    return JSON.parse(raw);
  } catch {
    // If the stored value is plain text or HTML rather than JSON
    // (legacy / corrupted), let TipTap interpret it as content.
    return raw;
  }
}

/* ── Component ─────────────────────────────────────────────────── */

const GoalDescriptionEditor: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
}) => {
  const { t } = useAppTranslation("pages");
  const effectivePlaceholder =
    placeholder ?? t("miniProject.cards.goal.descriptionPlaceholder");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, // goal descriptions are short pitches, no headings
      }),
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: effectivePlaceholder }),
    ],
    content: parseStoredContent(value),
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      // Serialize to JSON for description_rich storage. Empty doc
      // (just an empty paragraph) is treated as "no description" —
      // the storage column allows null.
      const json = ed.getJSON();
      const text = ed.getText().trim();
      if (!text) {
        onChange("");
      } else {
        onChange(JSON.stringify(json));
      }
    },
  });

  // Sync external value changes into the editor. Useful when the
  // parent loads a goal from the server after the editor has
  // mounted. Compare current content to avoid clobbering active
  // edits with stale props.
  useEffect(() => {
    if (!editor) return;
    const current = JSON.stringify(editor.getJSON());
    const incoming = value
      ? JSON.stringify(parseStoredContent(value))
      : "";
    // Empty editor's getJSON returns { type: 'doc', content: [{ type: 'paragraph' }] }
    // — a non-empty string. Compare safely to avoid loops.
    if (incoming && current !== incoming) {
      editor.commands.setContent(parseStoredContent(value) as object);
    }
  }, [value, editor]);

  // Sync the disabled prop too.
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  const handleLink = () => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const next = window.prompt(t("miniProject.cards.goal.linkPrompt"), prev || "");
    if (next === null) return;
    if (next === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: next }).run();
  };

  if (!editor) {
    return <div className={styles.skeleton} aria-hidden="true" />;
  }

  return (
    <div className={styles.editor}>
      <div className={styles.toolbar}>
        <button
          type="button"
          className={`${styles.toolBtn} ${editor.isActive("bold") ? styles.active : ""}`}
          onClick={() => editor.chain().focus().toggleBold().run()}
          aria-label={t("miniProject.cards.goal.toolbar.bold")}
          title={t("miniProject.cards.goal.toolbar.bold")}
          disabled={disabled}
        >
          <BoldIcon />
        </button>
        <button
          type="button"
          className={`${styles.toolBtn} ${editor.isActive("italic") ? styles.active : ""}`}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          aria-label={t("miniProject.cards.goal.toolbar.italic")}
          title={t("miniProject.cards.goal.toolbar.italic")}
          disabled={disabled}
        >
          <ItalicIcon />
        </button>
        <button
          type="button"
          className={`${styles.toolBtn} ${editor.isActive("link") ? styles.active : ""}`}
          onClick={handleLink}
          aria-label={t("miniProject.cards.goal.toolbar.link")}
          title={t("miniProject.cards.goal.toolbar.link")}
          disabled={disabled}
        >
          <LinkIcon />
        </button>
        <span className={styles.toolDivider} aria-hidden="true" />
        <button
          type="button"
          className={`${styles.toolBtn} ${editor.isActive("bulletList") ? styles.active : ""}`}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          aria-label={t("miniProject.cards.goal.toolbar.bulletList")}
          title={t("miniProject.cards.goal.toolbar.bulletList")}
          disabled={disabled}
        >
          <BulletListIcon />
        </button>
        <button
          type="button"
          className={`${styles.toolBtn} ${editor.isActive("orderedList") ? styles.active : ""}`}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          aria-label={t("miniProject.cards.goal.toolbar.orderedList")}
          title={t("miniProject.cards.goal.toolbar.orderedList")}
          disabled={disabled}
        >
          <OrderedListIcon />
        </button>
        <button
          type="button"
          className={`${styles.toolBtn} ${editor.isActive("blockquote") ? styles.active : ""}`}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          aria-label={t("miniProject.cards.goal.toolbar.quote")}
          title={t("miniProject.cards.goal.toolbar.quote")}
          disabled={disabled}
        >
          <QuoteIcon />
        </button>
      </div>
      <EditorContent editor={editor} className={styles.body} />
    </div>
  );
};

export default GoalDescriptionEditor;
